import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Paper, Stack, Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";

import { getMomentTz } from "@dexteel/mesf-core";
import { Campaign } from "../models/Campaign";

const moment = getMomentTz();

const formatNumber = (value: number | null | undefined, decimals: number) =>
	value == null
		? "-"
		: value.toLocaleString("en-US", {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			});

const formatHms = (seconds: number) => {
	const pad = (value: number) => String(value).padStart(2, "0");
	return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(Math.floor(seconds % 60))}`;
};

const formatDuration = (seconds: number | null) => {
	if (seconds == null) return "-";
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const HeaderValue = ({
	label,
	value,
	labelSize,
}: {
	label: string;
	value: ReactNode;
	labelSize?: number;
}) => (
	<Box component="span" sx={{ color: "text.secondary" }}>
		<Box component="span" sx={{ fontSize: labelSize }}>
			{label}
		</Box>{" "}
		<Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
			{value ?? "-"}
		</Box>
	</Box>
);

const Tile = ({
	label,
	value,
	unit,
	extra,
	alert = false,
}: {
	label: string;
	value: string | number;
	unit?: string;
	extra?: string;
	alert?: boolean;
}) => (
	<Box
		sx={(theme: Theme) => ({
			px: 1.25,
			py: 0.5,
			borderRadius: 1.5,
			bgcolor: alert
				? alpha(theme.palette.error.main, 0.08)
				: alpha(theme.palette.text.primary, 0.04),
			color: alert ? theme.palette.error.dark : theme.palette.text.primary,
		})}
	>
		<Typography
			sx={{
				fontSize: 11,
				lineHeight: 1.3,
				color: alert ? "inherit" : "text.secondary",
			}}
		>
			{label}
		</Typography>
		<Stack direction="row" alignItems="baseline" spacing={0.25}>
			<Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
				{value}
			</Typography>
			{unit && <Typography sx={{ fontSize: 11 }}>{unit}</Typography>}
			{extra && (
				<Typography sx={{ fontSize: 11, fontWeight: 600, pl: 0.75 }}>
					{extra}
				</Typography>
			)}
		</Stack>
	</Box>
);

const MaterialStat = ({
	label,
	billets,
	tons,
	alert = false,
}: {
	label: string;
	billets: number | null;
	tons: number | null;
	alert?: boolean;
}) => (
	<Box>
		<Typography color="text.secondary" sx={{ fontSize: 11 }}>
			{label}
		</Typography>
		<Typography
			sx={{ fontSize: 12, color: alert ? "error.dark" : "text.primary" }}
		>
			<b>{billets ?? "-"}</b> billets · <b>{formatNumber(tons, 3)}</b> tons
		</Typography>
	</Box>
);

const LegendItem = ({ color, label }: { color: string; label: string }) => (
	<Stack direction="row" alignItems="center" spacing={0.5}>
		<Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: color }} />
		<Typography color="text.secondary" sx={{ fontSize: 11 }}>
			{label}
		</Typography>
	</Stack>
);

export const CampaignSummaryCard = ({
	campaign,
	rolledOnly = false,
}: {
	campaign: Campaign;
	rolledOnly?: boolean;
}) => {
	const start = moment(campaign.Start);
	const end = campaign.End ? moment(campaign.End) : null;

	const charged = campaign.ChargedColdTons ?? 0;
	const rolled = campaign.RolledColdTons ?? 0;
	const rejected = campaign.RejectedColdTons ?? 0;
	const otherLosses = Math.max(charged - rolled - rejected, 0);
	const share = (tons: number) => `${(tons / charged) * 100}%`;
	const hasRejected = (campaign.RejectedBillets ?? 0) > 0 || rejected > 0;

	return (
		<Paper variant="outlined" sx={{ p: 1.5 }}>
			<Stack spacing={1.25}>
				<Stack
					direction="row"
					spacing={2}
					alignItems="flex-start"
					flexWrap="wrap"
					useFlexGap
				>
					<Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
						<Stack
							direction="row"
							alignItems="baseline"
							spacing={1.5}
							flexWrap="wrap"
							useFlexGap
						>
							<Typography
								sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}
							>
								{`Heat ${campaign.HeatName ?? "-"}`}
							</Typography>
							<Typography sx={{ fontSize: 16 }}>
								<HeaderValue
									label="Section"
									value={campaign.SectionName}
									labelSize={13}
								/>
							</Typography>
							<Typography sx={{ fontSize: 16 }}>
								<HeaderValue
									label="Grade"
									value={campaign.GradeName}
									labelSize={13}
								/>
							</Typography>
						</Stack>
						<Typography sx={{ fontSize: 12 }}>
							<HeaderValue label="Campaign" value={campaign.CampaignNumber} />
							{" · "}
							<HeaderValue label="Description" value={campaign.Description} />
							{" · "}
							<HeaderValue label="Billet Size" value={campaign.BilletSize} />
						</Typography>
					</Stack>

					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={(theme: Theme) => ({
							px: 1.25,
							py: 0.5,
							borderRadius: 1.5,
							bgcolor: alpha(theme.palette.text.primary, 0.04),
						})}
					>
						<AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
						<Typography sx={{ fontSize: 12 }}>
							{start.format("MM/DD/YY")} <b>{start.format("HH:mm")}</b>
							<Box component="span" sx={{ color: "text.secondary", mx: 0.75 }}>
								→
							</Box>
							{end ? (
								<>
									{!end.isSame(start, "day") && `${end.format("MM/DD/YY")} `}
									<b>{end.format("HH:mm")}</b>
								</>
							) : (
								<b>In progress</b>
							)}
						</Typography>
						<Box
							sx={{
								alignSelf: "stretch",
								borderLeft: 1,
								borderColor: "divider",
							}}
						/>
						<Typography sx={{ fontSize: 12, fontWeight: 700 }}>
							{formatDuration(campaign.Duration)}
						</Typography>
					</Stack>
				</Stack>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
						gap: 1,
					}}
				>
					<Tile
						label="Mill Yield"
						value={formatNumber(campaign.MillYield, 2)}
						unit={campaign.MillYield != null ? "%" : undefined}
					/>
					<Tile
						label="Finished Yield"
						value={formatNumber(campaign.FinishedYield, 2)}
						unit={campaign.FinishedYield != null ? "%" : undefined}
					/>
					<Tile
						label="Tons / hr"
						value={formatNumber(campaign.TonsPerHour, 2)}
					/>
					<Tile
						label="Delays"
						value={formatNumber(campaign.DelayTimePercent, 0)}
						unit={campaign.DelayTimePercent != null ? "%" : undefined}
						extra={
							campaign.TotalDelayTime != null
								? formatHms(campaign.TotalDelayTime)
								: undefined
						}
					/>
					<Tile
						label="Cobbles"
						value={campaign.Cobbles ?? 0}
						alert={(campaign.Cobbles ?? 0) > 0}
					/>
				</Box>

				<Stack spacing={0.75}>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
							gap: 2,
						}}
					>
						{!rolledOnly && (
							<MaterialStat
								label="Charged"
								billets={campaign.ChargedBillets}
								tons={campaign.ChargedColdTons}
							/>
						)}
						<MaterialStat
							label="Rolled"
							billets={campaign.RolledBillets}
							tons={campaign.RolledColdTons}
						/>
						{!rolledOnly && (
							<MaterialStat
								label="Rejected"
								billets={campaign.RejectedBillets}
								tons={campaign.RejectedColdTons}
								alert={hasRejected}
							/>
						)}
					</Box>
					{!rolledOnly && charged > 0 && (
						<>
							<Box
								sx={{
									display: "flex",
									height: 6,
									borderRadius: 3,
									overflow: "hidden",
									bgcolor: "action.hover",
								}}
							>
								<Box sx={{ width: share(rolled), bgcolor: "primary.main" }} />
								<Box sx={{ width: share(rejected), bgcolor: "error.main" }} />
								<Box sx={{ width: share(otherLosses), bgcolor: "grey.300" }} />
							</Box>
							<Stack direction="row" spacing={2}>
								<LegendItem color="primary.main" label="Rolled" />
								<LegendItem color="error.main" label="Rejected" />
								<LegendItem color="grey.300" label="Other losses" />
							</Stack>
						</>
					)}
				</Stack>
			</Stack>
		</Paper>
	);
};
